// src/components/Modals/BusinessOnboardingModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save, Building2, User, Mail, Lock, Phone as PhoneIcon } from 'lucide-react';
import { organizationsService, Organization, CreateBusinessRequest } from 'api/services/organizations.service';
import { useAuth } from 'hooks/useAuth';

interface BusinessOnboardingModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const BusinessOnboardingModal: React.FC<BusinessOnboardingModalProps> = ({ onClose, onSuccess }) => {
  const { currentOrganization } = useAuth();
  const [services, setServices] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState<CreateBusinessRequest>({
    businessName: '',
    businessDescription: '',
    businessId: '',
    serviceProviderId: currentOrganization?.type === 'service' ? currentOrganization.id : '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateBusinessRequest, string>>>({});

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoadingServices(true);
        const response = await organizationsService.getServiceProviders();
        if (response.success && response.data) {
          setServices(response.data);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      } finally {
        setLoadingServices(false);
      }
    };

    loadServices();
  }, []);

  const validate = () => {
    const newErrors: Partial<Record<keyof CreateBusinessRequest, string>> = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    if (!formData.businessId.trim()) {
      newErrors.businessId = 'Business ID is required';
    }
    if (!formData.serviceProviderId) {
      newErrors.serviceProviderId = 'Service provider is required';
    }
    if (!formData.adminFirstName.trim()) {
      newErrors.adminFirstName = 'First name is required';
    }
    if (!formData.adminLastName.trim()) {
      newErrors.adminLastName = 'Last name is required';
    }
    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Invalid email format';
    }
    if (!formData.adminPassword) {
      newErrors.adminPassword = 'Password is required';
    } else if (formData.adminPassword.length < 6) {
      newErrors.adminPassword = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await organizationsService.createBusiness(formData);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setErrorMessage(response.message || 'Failed to create business');
      }
    } catch (error: any) {
      console.error('Error creating business:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateBusinessRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setErrorMessage('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold">Onboard New Business</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Error Message */}
          {errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Business Information */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Information
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name *
              </label>
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => handleChange('businessName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.businessName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Acme Corporation"
              />
              {errors.businessName && (
                <p className="text-xs text-red-500 mt-1">{errors.businessName}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business ID *
                </label>
                <input
                  type="text"
                  value={formData.businessId}
                  onChange={(e) => handleChange('businessId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.businessId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ACME-001"
                />
                {errors.businessId && (
                  <p className="text-xs text-red-500 mt-1">{errors.businessId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Provider *
                </label>
                <select
                  value={formData.serviceProviderId}
                  onChange={(e) => handleChange('serviceProviderId', e.target.value)}
                  disabled={loadingServices || currentOrganization?.type === 'service'}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.serviceProviderId ? 'border-red-500' : 'border-gray-300'
                  } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                >
                  <option value="">Select Service Provider</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
                {errors.serviceProviderId && (
                  <p className="text-xs text-red-500 mt-1">{errors.serviceProviderId}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Description
              </label>
              <textarea
                value={formData.businessDescription}
                onChange={(e) => handleChange('businessDescription', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Brief description of the business"
                rows={3}
              />
            </div>
          </div>

          {/* Admin User Information */}
          <div className="space-y-4 border-t pt-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5" />
              Business Admin Details
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.adminFirstName}
                  onChange={(e) => handleChange('adminFirstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.adminFirstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John"
                />
                {errors.adminFirstName && (
                  <p className="text-xs text-red-500 mt-1">{errors.adminFirstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.adminLastName}
                  onChange={(e) => handleChange('adminLastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.adminLastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Doe"
                />
                {errors.adminLastName && (
                  <p className="text-xs text-red-500 mt-1">{errors.adminLastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address *
              </label>
              <input
                type="email"
                value={formData.adminEmail}
                onChange={(e) => handleChange('adminEmail', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.adminEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="john.doe@acme.com"
              />
              {errors.adminEmail && (
                <p className="text-xs text-red-500 mt-1">{errors.adminEmail}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => handleChange('adminPassword', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.adminPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Min. 6 characters"
                />
                {errors.adminPassword && (
                  <p className="text-xs text-red-500 mt-1">{errors.adminPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.adminPhone}
                  onChange={(e) => handleChange('adminPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="+1234567890"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="tl-btn tl-btn-primary disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Create Business
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessOnboardingModal;
